// BattleInboxBadge — small global counter that lives next to the
// IdentityPill in every header. Add-on. Auto-hides at zero.
//
// Pulses red when there are challenges literally awaiting your response.
// Goes amber when there are scheduled / outgoing items but nothing
// urgent. Disappears entirely when you have nothing in any bucket.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useBattlesEnabled } from "./useBattlesEnabled";

const POLL_MS = 300_000; // 5 min — was 30s, caused visible flicker on the dashboard

export default function BattleInboxBadge() {
  const { enabled } = useBattlesEnabled();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchInbox = async () => {
      try {
        const r = await api.get("/battles/inbox");
        if (!cancel) setData(r.data);
      } catch { /* feature flag could be off, or anon viewer */ }
    };
    fetchInbox();
    const t = setInterval(fetchInbox, POLL_MS);
    return () => { cancel = true; clearInterval(t); };
  }, [enabled]);

  if (!enabled) return null;
  if (!data) return null;
  const totalEntries =
    (data.awaiting_my_response?.length || 0) +
    (data.my_pending_outgoing?.length || 0) +
    (data.upcoming_scheduled?.length || 0) +
    (data.live_now?.length || 0);
  if (totalEntries === 0) return null;
  const urgent = (data.total_actionable || 0) > 0;

  return (
    <button
      type="button"
      onClick={() => nav("/wars")}
      data-testid="battle-inbox-badge"
      aria-label={`${totalEntries} silo war ${totalEntries === 1 ? "item" : "items"}`}
      title={urgent ? "Battles waiting on you" : "Silo Wars activity"}
      className={`inline-flex items-center gap-1 px-2 h-9 rounded-md border transition-all ${urgent
        ? "border-[#C16E5A] bg-[#C16E5A]/8 hover:bg-[#C16E5A]/15 animate-pulse"
        : "border-[#D8D6D2] bg-white hover:bg-[#FAFAFA]"}`}
    >
      <span className="text-base leading-none" style={{ color: urgent ? "#C16E5A" : "#A89360" }}>⚔</span>
      <span
        className={`min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full text-[10px] font-semibold px-1 ${urgent ? "bg-[#C16E5A] text-white" : "bg-[#1C1B1A]/8 text-[#1C1B1A]"}`}
        data-testid="battle-inbox-badge-count"
      >
        {totalEntries > 9 ? "9+" : totalEntries}
      </span>
    </button>
  );
}
